/**
 * <mothers-day-popup>
 * ---------------------------------------------------------------------------
 * Seasonal Mother's Day popup with phase-based content (rendered in Liquid).
 *
 * Behaviors
 *  - Delay timer : `desktop-delay` seconds on desktop, `mobile-delay` on mobile.
 *  - Alt trigger : show on the Nth page view in the current session
 *                  (`pageviews-trigger`, 0 disables).
 *  - Once-per-session gate (sessionStorage) + multi-day snooze (localStorage).
 *  - Coexistence with <newsletter-popup>:
 *      * subscriber / already-seen newsletter → start timer immediately
 *      * newsletter still pending            → wait for it to close, then timer
 *  - Editor preview : shows immediately in the theme editor.
 *
 * Storage keys
 *  - sessionStorage  'theme:md-popup-shown'   boolean flag (once per session)
 *  - sessionStorage  'theme:md-pageviews'     integer counter for pageviews
 *  - localStorage    'theme:md-popup-seen-at' ms timestamp of last display
 *  - (read-only) localStorage 'theme:popup-filled', 'theme:popup-appeared'
 *    are written by the existing newsletter popup; we read them to coordinate.
 */
(function () {
  'use strict';

  if (window.customElements.get('mothers-day-popup')) return;

  const MOBILE_MEDIA_QUERY = '(max-width: 749px)';

  const STORAGE_KEYS = {
    sessionShown: 'theme:md-popup-shown',
    sessionPageviews: 'theme:md-pageviews',
    localSeenAt: 'theme:md-popup-seen-at',
    newsletterFilled: 'theme:popup-filled',
    newsletterAppeared: 'theme:popup-appeared'
  };

  // Safe storage helpers (some contexts block access to storage)
  const safeGet = (storage, key) => {
    try { return storage.getItem(key); } catch (e) { return null; }
  };
  const safeSet = (storage, key, value) => {
    try { storage.setItem(key, value); } catch (e) { /* ignore */ }
  };

  class MothersDayPopup extends HTMLElement {
    #showTimer = null;
    #pendingObserver = null;
    #abortController = null;
    #previouslyFocused = null;
    #isOpen = false;
    #isForced = false;

    connectedCallback() {
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      // Detect URL-param QA bypass (?md-popup=1). Liquid already sets the
      // `force-show` attribute; we also re-check here as a belt-and-braces.
      this.#isForced = this.hasAttribute('force-show') || this.#hasUrlBypass();

      // Bind close triggers on data-md-close elements
      this.querySelectorAll('[data-md-close]').forEach((el) => {
        el.addEventListener('click', this.#onCloseClick, { signal });
      });

      // Keyboard: ESC to close
      this.addEventListener('keydown', this.#onKeydown, { signal });

      // CTA click: mark as seen and let the link navigate naturally.
      // In forced mode we skip persistence so QA reloads keep showing the popup.
      if (!this.#isForced) {
        const cta = this.querySelector('[data-md-cta]');
        if (cta) cta.addEventListener('click', this.#markAsSeen, { signal });
      }

      // Editor preview or URL bypass: show immediately, bypass all gating
      if (this.hasAttribute('editor-preview') || this.#isForced) {
        requestAnimationFrame(() => this.#open());
        return;
      }

      // Increment pageview counter (this page counts as a view)
      const pv = (parseInt(safeGet(sessionStorage, STORAGE_KEYS.sessionPageviews) || '0', 10) || 0) + 1;
      safeSet(sessionStorage, STORAGE_KEYS.sessionPageviews, String(pv)); 

      // Gating: once per session + N-day snooze
      if (!this.#isEligibleToShow()) return;

      // Alt trigger: if pageview threshold met, show sooner (after a brief settle)
      if (this.pageviewsTrigger > 0 && pv >= this.pageviewsTrigger) {
        this.#scheduleShow(1500);
        return;
      }

      // Coexistence with newsletter popup
      if (this.coexistWithNewsletter && this.#isNewsletterPending()) {
        this.#waitForNewsletterClose(() => this.#scheduleShow(this.delayMs));
      } else {
        this.#scheduleShow(this.delayMs);
      }
    }

    disconnectedCallback() {
      if (this.#showTimer) clearTimeout(this.#showTimer);
      if (this.#pendingObserver) this.#pendingObserver.disconnect();
      if (this.#abortController) this.#abortController.abort();
    }

    // -----------------------------------------------------------------------
    // Attribute-backed getters
    // -----------------------------------------------------------------------
    get desktopDelayMs() {
      return (parseInt(this.getAttribute('desktop-delay') || '30', 10) || 30) * 1000;
    }
    get mobileDelayMs() {
      return (parseInt(this.getAttribute('mobile-delay') || '50', 10) || 50) * 1000;
    }
    get delayMs() {
      return this.#isMobile() ? this.mobileDelayMs : this.desktopDelayMs;
    }
    get pageviewsTrigger() {
      return parseInt(this.getAttribute('pageviews-trigger') || '0', 10) || 0;
    }
    get hideDays() {
      return parseInt(this.getAttribute('hide-days') || '7', 10) || 0;
    }
    get oncePerSession() {
      return this.hasAttribute('once-per-session');
    }
    get coexistWithNewsletter() {
      return this.hasAttribute('coexist-newsletter');
    }

    // -----------------------------------------------------------------------
    // Eligibility
    // -----------------------------------------------------------------------
    #isMobile() {
      return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    }

    #isEligibleToShow() {
      // Once per session
      if (this.oncePerSession && safeGet(sessionStorage, STORAGE_KEYS.sessionShown) === 'true') {
        return false;
      }
      // Snooze window (N days since last display)
      const seenAt = parseInt(safeGet(localStorage, STORAGE_KEYS.localSeenAt) || '0', 10);
      if (seenAt && this.hideDays > 0) {
        const ageMs = Date.now() - seenAt;
        const snoozeMs = this.hideDays * 24 * 60 * 60 * 1000;
        if (ageMs < snoozeMs) return false;
      }
      return true;
    }

    // -----------------------------------------------------------------------
    // Newsletter coexistence
    // -----------------------------------------------------------------------
    #isNewsletterPending() {
      // If the user already filled the newsletter, they're a subscriber → no wait
      if (safeGet(localStorage, STORAGE_KEYS.newsletterFilled) === 'true') return false;
      // If the newsletter popup already appeared in a prior session, no wait
      if (safeGet(localStorage, STORAGE_KEYS.newsletterAppeared) === 'true') return false;
      // Newsletter popup must exist in the DOM and be eligible to show
      return document.querySelector('newsletter-popup') !== null;
    }

    #waitForNewsletterClose(callback) {
      const nl = document.querySelector('newsletter-popup');
      if (!nl) { callback(); return; }

      let opened = false;
      this.#pendingObserver = new MutationObserver(() => {
        const hasOpen = nl.hasAttribute('open');
        if (hasOpen) opened = true;
        if (opened && !hasOpen) {
          if (this.#pendingObserver) {
            this.#pendingObserver.disconnect();
            this.#pendingObserver = null;
          }
          callback();
        }
      });
      this.#pendingObserver.observe(nl, { attributes: true, attributeFilter: ['open'] });

      // Safety fallback: if newsletter never opens within 10 minutes, proceed anyway.
      setTimeout(() => {
        if (this.#pendingObserver) {
          this.#pendingObserver.disconnect();
          this.#pendingObserver = null;
          callback();
        }
      }, 10 * 60 * 1000);
    }

    // -----------------------------------------------------------------------
    // Scheduling
    // -----------------------------------------------------------------------
    #scheduleShow(delayMs) {
      if (this.#showTimer) clearTimeout(this.#showTimer);
      this.#showTimer = setTimeout(() => this.#showIfStillEligible(), Math.max(0, delayMs));
    }

    #showIfStillEligible() {
      if (!this.#isEligibleToShow()) return;
      // Don't overlap: if newsletter is currently open, wait again
      const openedNewsletter = document.querySelector('newsletter-popup[open]');
      if (openedNewsletter) {
        this.#waitForNewsletterClose(() => this.#scheduleShow(this.delayMs));
        return;
      }
      this.#open();
    }

    // -----------------------------------------------------------------------
    // Open / close
    // -----------------------------------------------------------------------
    #open = () => {
      if (this.#isOpen) return;
      this.#isOpen = true;

      this.#previouslyFocused = document.activeElement;
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('mothers-day-popup-open');

      // Focus the close button for accessibility
      const closeBtn = this.querySelector('.mothers-day-popup__close');
      if (closeBtn) {
        // Defer to allow transition to start
        setTimeout(() => closeBtn.focus(), 50);
      }

      // Emit event for analytics / other listeners
      this.dispatchEvent(new CustomEvent('mothers-day-popup:open', { bubbles: true }));

      // Persist gating flags (skip in forced/QA mode so reloads keep showing it)
      if (!this.#isForced) {
        this.#markAsSeen();
      }
    };

    #hasUrlBypass() {
      try {
        return /(?:^|[?&])md-popup=1(?:&|$)/.test(window.location.search || '');
      } catch (e) {
        return false;
      }
    }

    #close = () => {
      if (!this.#isOpen) return;
      this.#isOpen = false;

      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('mothers-day-popup-open');

      this.dispatchEvent(new CustomEvent('mothers-day-popup:close', { bubbles: true }));

      if (this.#previouslyFocused && typeof this.#previouslyFocused.focus === 'function') {
        try { this.#previouslyFocused.focus(); } catch (e) { /* ignore */ }
      }
    };

    #markAsSeen = () => {
      safeSet(sessionStorage, STORAGE_KEYS.sessionShown, 'true');
      safeSet(localStorage, STORAGE_KEYS.localSeenAt, String(Date.now()));
    };

    // -----------------------------------------------------------------------
    // Event handlers
    // -----------------------------------------------------------------------
    #onCloseClick = (event) => {
      event.preventDefault();
      this.#close();
    };

    #onKeydown = (event) => {
      if (event.key === 'Escape' && this.#isOpen) {
        event.preventDefault();
        this.#close();
      }
    };
  }

  window.customElements.define('mothers-day-popup', MothersDayPopup);
})();
